import { useTranslation } from 'react-i18next';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';

import { useScheduleFormat } from '../hooks/use-schedule-format';
import { useScheduleLifecycle } from '../hooks/use-schedule-mutations';
import { firstRunAtOf } from '../schemas/schedule-rules';

import { cadenceEvery } from './cadence-text';
import { ScheduleErrorAlert } from './schedule-error-alert';

export interface ActivateScheduleDialogProps {
  schedule: ProductSchedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Says what activating starts before it starts it: the stores, how many are live at a time, the
 * cadence in plain words, the clock it runs on and when the first run can be. A refusal (no
 * stores, no product that can go live, an end date already past) stays in the dialog.
 */
export function ActivateScheduleDialog({
  schedule,
  open,
  onOpenChange,
}: ActivateScheduleDialogProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const activate = useScheduleLifecycle(schedule.id, 'activate');
  const format = useScheduleFormat(schedule.timeZone);
  // Worked out by the backend for a draft; the start bound shows only if it is missing.
  const firstRunAt = firstRunAtOf(schedule);
  const close = () => {
    activate.reset();
    onOpenChange(false);
  };
  const facts = [
    schedule.stores.length > 0
      ? t('schedules:activate.stores', {
          stores: schedule.stores.map((s) => s.storeName).join(', '),
        })
      : null,
    t('schedules:activate.live', { count: schedule.liveCount }),
    schedule.members.pinned > 0
      ? t('schedules:activate.pinned', { count: schedule.members.pinned })
      : null,
    t('schedules:activate.cadence', { every: cadenceEvery(t, schedule) }),
    t('schedules:activate.zone', { zone: schedule.timeZone }),
    firstRunAt
      ? t('schedules:activate.firstRun', {
          when: format.dateTime(firstRunAt),
          zone: schedule.timeZone,
        })
      : t('schedules:activate.start', { start: format.dateTime(schedule.startsAt) }),
    t('schedules:activate.pool', { count: schedule.members.total }),
  ].filter((fact): fact is string => fact !== null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
        else onOpenChange(true);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('schedules:activate.title', { name: schedule.name })}</DialogTitle>
          <DialogDescription>{t('schedules:activate.description')}</DialogDescription>
        </DialogHeader>
        {activate.isError ? <ScheduleErrorAlert error={activate.error} action="activate" /> : null}
        <ul className="list-disc space-y-1 pl-5 text-sm">
          {facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={activate.isPending} onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={activate.isPending}
            onClick={() => activate.mutate(undefined, { onSuccess: close })}
            data-tour="schedules.detail.activate-confirm"
          >
            {activate.isPending
              ? t('schedules:activate.activating')
              : t('schedules:activate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
